import React, {useState, useEffect} from "react";
import styled, {keyframes} from "styled-components";
import {motion} from "framer-motion";
import Navigation from "../Navigation";
import Header from "../Header";
import {postInitEvent} from "../../../util/actions/saveCustomEvent";
import ReactHtmlParser from "react-html-parser";

const DiagnosticQuestionPage = ({
  page,
  setPage,
  answersToSelect,
  selectedAnswers,
  setSelectedAnswers,
  pageIndex,
  text,
  isOptional,
  eventName,
  playerId,
}) => {
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  const handleSelectItem = (newItemIndex) => {
    if (isOptional) {
      // Jeśli opcja jest ustawiona na true, dodaj/zaktualizuj wybraną odpowiedź
      if (selectedItemIds.includes(newItemIndex)) {
        // Odznacz odpowiedź, jeśli została już zaznaczona
        setSelectedItemIds((prevItemIds) =>
          prevItemIds.filter((id) => id !== newItemIndex)
        );
      } else {
        // Zaznacz odpowiedź, jeśli nie została jeszcze zaznaczona
        setSelectedItemIds((prevItemIds) => [...prevItemIds, newItemIndex]);
      }
    } else {
      // Jeśli opcja jest ustawiona na false, zaktualizuj tylko jedną wybraną odpowiedź
      setSelectedItemIds([newItemIndex]);
    }
  };

  const parseHighlightTag = (string) => {
    let stringParsed = string.replace(
      /\[highlight\]/g,
      `<span class="highlight-title">`
    );
    stringParsed = stringParsed.replace(/\[\/highlight\]/g, `</span>`);
    return stringParsed;
  }

  useEffect(() => {
    if (playerId > 0) {
      postInitEvent(`${eventName}_ENTER`, null, null, playerId)
    }
  }, [playerId]);

  useEffect(() => {
    const copyOfSelectedAnswers = [...selectedAnswers];

    if (selectedItemIds.length > 0) {
      const selectedAnswerIds = selectedItemIds.map(
        (itemIndex) => answersToSelect[itemIndex].id
      );
      copyOfSelectedAnswers[pageIndex] = selectedAnswerIds;
    } else {
      copyOfSelectedAnswers[pageIndex] = undefined;
    }

    setSelectedAnswers(copyOfSelectedAnswers);
  }, [selectedItemIds]);

  useEffect(() => {
    const isDiagnosticQuestionAlreadyAnswered = !!selectedAnswers[pageIndex];
    if (isDiagnosticQuestionAlreadyAnswered) {
      const selectedAnswerIds = selectedAnswers[pageIndex];
      const indexOfSelectedItems = selectedAnswerIds.map((answerId) =>
        answersToSelect.findIndex((answer) => answer.id === answerId)
      );

      if (indexOfSelectedItems.length > 0) {
        setSelectedItemIds(indexOfSelectedItems);
      }
    }
  }, []);

  const prepareText = parseHighlightTag(text);

  return (
    <ViewContainer
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={{duration: 0.5}}
    >
      <Header/>
      <Content>
        <TextContent>
          <Text>{ReactHtmlParser(prepareText)}</Text>
        </TextContent>
        <SelectBoxContainer>
          {answersToSelect.map((answer, index) => (
            <SelectBox
              key={index}
              onClick={() => handleSelectItem(index)}
              isSelected={selectedItemIds.includes(index)}
            >
              {answer.text}
            </SelectBox>
          ))}
        </SelectBoxContainer>
        <Navigation
          showBackButton={true}
          showNextButton={selectedItemIds.length > 0}
          setPage={setPage}
          page={page}
          nextButtonCallback={() => {
            postInitEvent(`${eventName}_ANSWER`, {question: text, answersToSelect: answersToSelect}, {
              answersSelectIds: selectedItemIds,
              answerSelected: answersToSelect.filter((e, i) => selectedItemIds.includes(i))
            }, playerId)
          }
          }
        />
      </Content>
    </ViewContainer>
  );
};

export default DiagnosticQuestionPage;

const ViewContainer = styled(motion.div)`
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
`;

const Content = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 0 30px 0;
`;

const Text = styled.div`
  text-align: center;
  color: #fff;
  font-size: 20px;
  line-height: 35px;
  font-weight: 300;

  span.highlight-title {
    font-weight: bold;
    color: #FFF78A;
  }
`;

const SmallText = styled(Text)`
  font-size: 20px;
  line-height: 25px;
`;

const SelectBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  width: 100%;
`;

const growAnimation = keyframes`
  0% {
    scale: 0.9
  }
  100% {
    scale: 1
  }
`;

const SelectBox = styled.div`
  width: 100%;
  background-color: ${(props) =>
          props.isSelected ? "rgba(0, 0, 0, 0.5)" : "rgba(57,56,146,0.8)"};
  padding: 15px;
  border-radius: 15px;
  text-align: center;
  color: #fff;
  font-size: 17px;
  font-weight: 300;
  transition: all 0.2s;
  animation: ${growAnimation} 0.5s ease;

  &:active {
    transform: scale(0.97);
  }
`;
